import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import {
  fetchCreditSummary,
  hasSentWallNoteThisMonth,
  sendWallNote,
  wallNoteCost,
} from "../../services/creditsService";
import { formatCount } from "../../utils/formatCount";
import "./WallNoteModal.css";

const MIN_LEN = 250;
const MAX_LEN = 500;

export default function WallNoteModal({
  open,
  onClose,
  creatorId,
  creatorName = "this creator",
  onSuccess,
}) {
  const { currentUser, userProfile } = useAuth();
  const isCreator = userProfile?.role === "creator" || userProfile?.isCreator;
  const cost = wallNoteCost(isCreator);

  const [text, setText] = useState("");
  const [balance, setBalance] = useState(null);
  const [alreadySent, setAlreadySent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open || !currentUser) return;
    setText("");
    setError("");
    setSuccess(false);
    fetchCreditSummary(currentUser.uid, isCreator).then((s) => setBalance(s.balance));
    if (creatorId) {
      hasSentWallNoteThisMonth(creatorId, currentUser.uid).then(setAlreadySent);
    }
  }, [open, currentUser, creatorId, isCreator]);

  if (!open) return null;

  const len = text.trim().length;
  const validLen = len >= MIN_LEN && len <= MAX_LEN;
  const canAfford = balance == null || balance >= cost;
  const firstName = creatorName.split(" ")[0] || "them";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser || !creatorId || alreadySent || busy || !validLen || !canAfford) return;
    setError("");
    setBusy(true);
    try {
      await sendWallNote(creatorId, text.trim());
      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      setError(err.message || "Could not send wall note.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="wnm-backdrop" onClick={onClose} role="presentation">
      <div
        className="wnm-sheet"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="wnm-title"
      >
        <button type="button" className="wnm-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        <span className="wnm-eyebrow">Appreciation wall</span>
        <h2 id="wnm-title" className="wnm-title">
          Send a note to <em>{firstName}</em>
        </h2>
        <p className="wnm-sub">
          {cost} credits · {MIN_LEN}–{MAX_LEN} characters · max 1 per creator per month
        </p>

        {!currentUser ? (
          <div className="wnm-auth">
            <p>Sign in to send an appreciation wall note.</p>
            <Link to="/welcome?auth=signin" className="wnm-btn wnm-btn--primary">
              Sign in
            </Link>
          </div>
        ) : success ? (
          <div className="wnm-success">
            <p>Your note was sent! {firstName} will review it before it appears on their wall.</p>
            <button type="button" className="wnm-btn wnm-btn--primary" onClick={onClose}>
              Done
            </button>
          </div>
        ) : alreadySent ? (
          <div className="wnm-success">
            <p>You already sent a wall note to {firstName} this month.</p>
            <button type="button" className="wnm-btn wnm-btn--ghost" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <textarea
              className="wnm-textarea"
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
              placeholder={`Write something meaningful for ${firstName}…`}
              rows={6}
              maxLength={MAX_LEN}
            />
            <div className="wnm-meta">
              <span className={len < MIN_LEN ? "wnm-count--warn" : ""}>
                {len} / {MAX_LEN}
                {len > 0 && len < MIN_LEN ? ` · ${MIN_LEN - len} more needed` : ""}
              </span>
              {balance != null && (
                <span className={!canAfford ? "wnm-count--warn" : ""}>
                  Balance: {formatCount(balance)} cr
                </span>
              )}
            </div>
            {error && <p className="wnm-error">{error}</p>}
            {!canAfford && balance != null && (
              <p className="wnm-error">
                You need {cost - balance} more credits.{" "}
                <Link to="/perks">Earn or buy credits →</Link>
              </p>
            )}
            <div className="wnm-actions">
              <button type="button" className="wnm-btn wnm-btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="wnm-btn wnm-btn--primary"
                disabled={busy || !validLen || !canAfford}
              >
                {busy ? "Sending…" : `Send · ${cost} cr`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function WallNoteTriggerButton({ creator, onOpen, className = "" }) {
  const { userProfile } = useAuth();
  const isCreator = userProfile?.role === "creator" || userProfile?.isCreator;
  const cost = wallNoteCost(isCreator);
  const firstName = creator?.displayName?.split(" ")[0] || "them";
  return (
    <button type="button" className={`wnm-trigger ${className}`} onClick={onOpen}>
      <span className="wnm-trigger-title">Send wall note</span>
      <span className="wnm-trigger-sub">
        {cost} cr · appreciation for {firstName}
      </span>
    </button>
  );
}
